import { useReducer } from 'react';
import { Modal, Steps } from 'antd';
import { wizardReducer, initialState } from './wizardReducer';
import Step1Upload from './steps/Step1Upload';
import Step2MapColumns from './steps/Step2MapColumns';
import Step3MapEmployees from './steps/Step3MapEmployees';
import Step4PreviewConfirm from './steps/Step4PreviewConfirm';

interface SmartImportWizardProps {
	open: boolean;
	payPeriodId: string | null;
	payPeriodLabel?: string;
	onClose: () => void;
	// ENG-374: forwards the labels of fields newly created during the import so the
	// page can prompt the user to map their GL accounts after the wizard closes.
	onImported?: (newFieldLabels?: string[]) => void;
	// Pre-supplied file (e.g. attached in the AI chatbot) — see Step1Upload.
	initialFile?: File | null;
}

const STEP_ITEMS = [
	{ title: 'Upload' },
	{ title: 'Map Columns' },
	{ title: 'Map Employees' },
	{ title: 'Preview' },
];

/**
 * Smart Import wizard shell (WS-8). Owns the reducer, renders the Ant Modal,
 * the Steps indicator, and the active step. Steps 2-4 are added by WS-9/10/11.
 */
export default function SmartImportWizard({
	open,
	payPeriodId,
	payPeriodLabel,
	onClose,
	onImported,
	initialFile,
}: SmartImportWizardProps) {
	const [state, dispatch] = useReducer(wizardReducer, initialState);
	const companyId = localStorage.getItem('companyId') ?? '';

	const handleClose = () => {
		dispatch({ type: 'RESET' });
		onClose();
	};

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			title="Import Employee Costs"
			width={1200}
			centered
			maskClosable={false}
			footer={null}
			destroyOnHidden
			styles={{
				// ENG-383's sticky Steps header / Step2 footer only work if this body
				// actually scrolls — `overflow: hidden` silently clips anything past
				// 85vh with no way to reach it (no scrollbar at all at this level),
				// which defeats a step's own internal scroll region (e.g. Step 2's
				// column table) whenever the surrounding chrome + that region's
				// height together exceed the viewport. `auto` makes this the
				// guaranteed fallback: even if a step's own scroll area is sized
				// wrong for some content shape, the whole body can still scroll to
				// reach it.
				body: {
					maxHeight: '85vh',
					overflowY: 'auto',
					display: 'flex',
					flexDirection: 'column',
				},
			}}
		>
			<Steps
				current={state.currentStep}
				items={STEP_ITEMS}
				style={{ marginBottom: 24, position: 'sticky', top: 0, background: 'white', zIndex: 1 }}
			/>
			{state.currentStep === 0 && (
				<Step1Upload
					state={state}
					dispatch={dispatch}
					companyId={companyId}
					payPeriodId={payPeriodId}
					onClose={handleClose}
					initialFile={initialFile}
				/>
			)}
			{state.currentStep === 1 && (
				<Step2MapColumns
					state={state}
					dispatch={dispatch}
					companyId={companyId}
					payPeriodId={payPeriodId}
					onClose={handleClose}
				/>
			)}
			{state.currentStep === 2 && (
				<Step3MapEmployees
					state={state}
					dispatch={dispatch}
					companyId={companyId}
					payPeriodId={payPeriodId}
					onBack={() => dispatch({ type: 'BACK_STEP' })}
					onCancel={handleClose}
				/>
			)}
			{state.currentStep === 3 && (
				<Step4PreviewConfirm
					state={state}
					dispatch={dispatch}
					payPeriodId={payPeriodId ?? ''}
					payPeriodLabel={payPeriodLabel ?? ''}
					companyId={companyId}
					onBack={() => dispatch({ type: 'BACK_STEP' })}
					onCancel={handleClose}
					onImported={(newFieldLabels) => {
						dispatch({ type: 'RESET' });
						onImported?.(newFieldLabels);
					}}
				/>
			)}
		</Modal>
	);
}
